import { useEffect, useMemo, useState } from "react";
import { useLocalizedNavigate as useNavigate } from "@/hooks/useLocalizedNavigate";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, Loader2, Compass } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { Seo } from "@/components/seo/Seo";
import { useLocalizedField } from "@/hooks/useLocalizedField";
import { cn } from "@/lib/utils";

const STAGES = ["pre_pmf", "growth", "scale"] as const;
type Stage = (typeof STAGES)[number];

interface TrackRow {
  id: string;
  slug: string;
  stage: string | null;
  icon: string | null;
  title_en: string | null;
  title_es: string | null;
  title_fr: string | null;
  title_pt: string | null;
  description_en: string | null;
  description_es: string | null;
  description_fr: string | null;
  description_pt: string | null;
}

const OnboardingTracks = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const localized = useLocalizedField();
  const [stage, setStage] = useState<Stage | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Pre-fill from existing profile
  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("stage, preferred_track_ids")
        .eq("id", u.user.id)
        .maybeSingle();
      if (profile?.stage && (STAGES as readonly string[]).includes(profile.stage)) {
        setStage(profile.stage as Stage);
      }
      if (profile?.preferred_track_ids?.length) {
        setPicked(profile.preferred_track_ids);
      }
    })();
  }, []);

  const { data: tracks, isLoading } = useQuery<TrackRow[]>({
    queryKey: ["onboarding-tracks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tracks")
        .select(
          "id,slug,stage,icon,title_en,title_es,title_fr,title_pt,description_en,description_es,description_fr,description_pt",
        )
        .eq("published", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as TrackRow[];
    },
  });

  const visibleTracks = useMemo(() => {
    if (!tracks) return [];
    if (!stage) return tracks;
    // show tracks matching stage first, then others
    return [...tracks].sort((a, b) => {
      const am = a.stage === stage ? 0 : 1;
      const bm = b.stage === stage ? 0 : 1;
      return am - bm;
    });
  }, [tracks, stage]);

  const togglePick = (id: string) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const canContinue = stage !== null && picked.length >= 1;

  const handleContinue = async () => {
    if (!canContinue) return;
    setSaving(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      navigate("/login", { replace: true });
      return;
    }
    const { error } = await supabase
      .from("profiles")
      .update({ stage, preferred_track_ids: picked })
      .eq("id", u.user.id);
    setSaving(false);
    if (error) {
      toast.error(t("onboarding.errors.saveFailed"));
      return;
    }
    navigate("/onboarding/diagnostic");
  };

  return (
    <>
      <Seo title={t("onboarding.tracks.seoTitle")} />
      <OnboardingShell
        step={2}
        showSkip
        onSkip={async () => {
          const { data: u } = await supabase.auth.getUser();
          if (u.user) {
            await supabase
              .from("profiles")
              .update({ onboarding_completed_at: new Date().toISOString() })
              .eq("id", u.user.id);
          }
          navigate("/dashboard", { replace: true });
        }}
      >
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
            {t("onboarding.tracks.title")}
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            {t("onboarding.tracks.subtitle")}
          </p>
        </div>

        <section className="mx-auto mt-8 max-w-3xl">
          <h2 className="text-lg font-semibold">{t("onboarding.tracks.stageLabel")}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {STAGES.map((s) => {
              const isSel = stage === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStage(s)}
                  aria-pressed={isSel}
                  className={cn(
                    "rounded-xl border-2 p-4 text-left transition-all",
                    isSel ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        "h-4 w-4 rounded-full border-2 transition-colors",
                        isSel ? "border-primary bg-primary" : "border-muted-foreground/30",
                      )}
                    />
                    <span className="font-semibold">{t(`onboarding.tracks.stages.${s}.label`)}</span>
                  </div>
                  <p className="mt-2 pl-6 text-xs text-muted-foreground">
                    {t(`onboarding.tracks.stages.${s}.desc`)}
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        <section className="mx-auto mt-8 max-w-3xl">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold">{t("onboarding.tracks.tracksLabel")}</h2>
            <span className="text-xs text-muted-foreground">
              {t("onboarding.tracks.selected", { count: picked.length })}
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.tracks.tracksHint")}</p>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {isLoading ? (
              [0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)
            ) : visibleTracks.length === 0 ? (
              <div className="col-span-full">
                <EmptyState
                  illustration={<Compass className="h-10 w-10" />}
                  title={t("onboarding.tracks.empty")}
                />
              </div>
            ) : (
              visibleTracks.map((tr) => {
                const isSel = picked.includes(tr.id);
                const title = localized(tr, "title");
                const desc = localized(tr, "description");
                return (
                  <button
                    key={tr.id}
                    type="button"
                    onClick={() => togglePick(tr.id)}
                    aria-pressed={isSel}
                    className={cn(
                      "rounded-xl border-2 p-4 text-left transition-all",
                      isSel ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold">{title}</h3>
                      <div
                        className={cn(
                          "grid h-5 w-5 flex-shrink-0 place-items-center rounded border-2 transition-colors",
                          isSel ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/30",
                        )}
                      >
                        {isSel ? <Check className="h-3 w-3" /> : null}
                      </div>
                    </div>
                    {desc ? <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{desc}</p> : null}
                  </button>
                );
              })
            )}
          </div>
        </section>

        <div className="mx-auto mt-10 flex max-w-3xl items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/onboarding/persona")} disabled={saving}>
            <ArrowLeft className="mr-1 h-4 w-4" /> {t("common.back")}
          </Button>
          <div className="flex items-center gap-3">
            {!canContinue && (
              <span className="hidden text-xs font-mono text-muted-foreground sm:inline">
                {t("onboarding.tracks.minHint")}
              </span>
            )}
            <Button onClick={handleContinue} disabled={!canContinue || saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {t("common.next")} <ArrowRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </OnboardingShell>
    </>
  );
};

export default OnboardingTracks;
